import { useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth-context';
import { ApiProvider } from './ApiProvider';
import type { ApiConfig } from './client';
import type { RuntimeConfig } from './config';

export interface AuthedApiProviderProps {
  runtime: RuntimeConfig;
  /** The §4 mock fetch, supplied only when `VITE_USE_MOCK=1`. */
  mockFetch?: typeof fetch;
  children: ReactNode;
}

/**
 * Bridges the AuthProvider (dev token / login state) into the ApiProvider so
 * every request carries `Authorization: Bearer <token>` and a server 401 clears
 * the session (which surfaces the login prompt). Kept separate so ApiProvider
 * stays a pure config→client adapter and ChessRepsApi stays independently
 * testable.
 */
export function AuthedApiProvider({
  runtime,
  mockFetch,
  children,
}: AuthedApiProviderProps): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();

  // Query keys are not per user: when the session ends (logout or a 401), drop the
  // cache so the next sign-in in this tab never shows the previous user's data.
  useEffect(() => {
    if (!auth.token) queryClient.clear();
  }, [auth.token, queryClient]);

  const config = useMemo<ApiConfig>(
    () => ({
      baseUrl: runtime.apiOrigin,
      workspaceId: auth.workspaceId ?? runtime.workspaceId,
      getAccessToken: () => auth.token,
      onUnauthorized: auth.logout,
      onRefresh: auth.refresh,
      fetch: mockFetch,
    }),
    [
      runtime.apiOrigin,
      runtime.workspaceId,
      auth.workspaceId,
      auth.token,
      auth.logout,
      auth.refresh,
      mockFetch,
    ],
  );

  return <ApiProvider config={config}>{children}</ApiProvider>;
}
